import { useIntl } from 'react-intl';

import type {
  MediaWrapperContainerTypeEnum,
  MediaWrapperTypeEnum,
} from '@/utils/aspectRatioUtils';
import { getAspectRatioForImages } from '@/utils/aspectRatioUtils';
import { getMediaWrapperAriaLabel } from '@/utils/mediaAriaLabel';
import { videoLoader } from '@/utils/videoLoader';

import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Video } from '@/components/Video';
import type {
  ContentfulAsset,
  ContentfulImageWithFocalPointFragment,
} from '@/graphql/contentful/api';
import { useImageLoader } from '@/hooks/useImageLoader';
import { useIsMdDown } from '@/hooks/useMedia';
import { useRegionProvider } from '@/providers/RegionProvider';

import { DesktopImage, MobileImage } from './components';

import * as S from './MediaWrapper.styles';

const collectAssets = (
  ...candidates: Array<ContentfulAsset | null | undefined>
) => candidates.filter((asset): asset is ContentfulAsset => Boolean(asset));

const resolvePlaybackVideo = <T,>(
  useVideo: boolean,
  isMobileView: boolean,
  desktopVideo: T | null | undefined,
  mobileVideo: T | null | undefined,
) => {
  if (!useVideo) {
    return null;
  }
  if (isMobileView && mobileVideo) {
    return mobileVideo;
  }
  return desktopVideo ?? null;
};

export interface MediaWrapperProps extends ContentfulImageWithFocalPointFragment {
  aspectRatio?: { x: number; y: number };
  cardGroupType?: string;
  className?: string;
  isCountDownType?: boolean;
  lazyLoadVideo?: boolean;
  loaderProps?: { height: number };
  mediaWrapperType?: string;
  position?: string;
  preload?: boolean;
  setLoadingState?: (state: string) => void;
  sizes?: string;
  useAspectRatio?: boolean;
  useLoader?: boolean;
  useVideo?: boolean;
  width?: string;
  widths?: number[];
}

export const MediaWrapper = (props: MediaWrapperProps) => {
  const {
    alt,
    cardGroupType,
    className,
    focalPoint,
    image,
    imageMobile,
    imageOnHover,
    imageOnHoverMobile,
    internalName,
    isCountDownType = false,
    lazyLoadVideo,
    loaderProps,
    mediaWrapperType,
    preload,
    setLoadingState,
    sizes,
    url,
    useLoader,
    useVideo = true,
    video,
    videoMobile,
    videoPlayOption,
    width,
  } = props;
  const { formatMessage } = useIntl();
  const {
    locale: { code: languageCode },
  } = useRegionProvider();
  const { load: imageLoader } = useImageLoader();
  const isMobile = useIsMdDown();

  const mobileImageFocalPoint = {
    focalPoint: {
      x: ((imageMobile?.width ?? 0) as number) / 2,
      y: ((imageMobile?.height ?? 0) as number) / 2,
    },
  };

  const _imageDesktop = collectAssets(
    image as ContentfulAsset,
    imageOnHover as ContentfulAsset,
  );
  const _imageMobile = collectAssets(
    (imageMobile ?? image) as ContentfulAsset,
    (imageOnHoverMobile ?? imageOnHover) as ContentfulAsset,
  );
  const _video = resolvePlaybackVideo(useVideo, isMobile, video, videoMobile);
  const posterDevice = isMobile ? 'mobile' : 'desktop';
  const getAspectRatioData = (deviceType?: string) => {
    const image = deviceType === 'mobile' ? _imageMobile[0] : _imageDesktop[0];
    return (
      getAspectRatioForImages(
        mediaWrapperType as MediaWrapperTypeEnum,
        cardGroupType as MediaWrapperContainerTypeEnum,
        image?.width,
        image?.height,
        deviceType,
      ) ?? { x: 1, y: 1 }
    );
  };

  const getOptimizedImage = (deviceType?: string) => {
    const image = deviceType === 'mobile' ? _imageMobile[0] : _imageDesktop[0];
    const imageFocalPoint =
      deviceType === 'mobile' ? mobileImageFocalPoint : focalPoint;
    const aspectRatio = getAspectRatioData(deviceType);
    const rawWidth = image?.width as number | undefined;
    const posterWidth =
      Number.isFinite(rawWidth) && (rawWidth as number) > 0
        ? (rawWidth as number)
        : 1280;

    if (!image?.url) {
      return '';
    }

    return imageLoader({
      focalPoint: imageFocalPoint?.focalPoint,
      aspectRatioWidth: aspectRatio.x,
      aspectRatioHeight: aspectRatio.y,
      originalImageDimensions: {
        x: image.width as number,
        y: image.height as number,
      },
      width: posterWidth,
      src: image.url,
    });
  };

  const poster = _video ? getOptimizedImage(posterDevice) : '';
  const videoQuality = isMobile ? 'eco' : undefined;
  const videoUrl = _video?.url ? videoLoader(_video.url, videoQuality) : '';

  const imageProps = {
    alt: alt || internalName,
    className,
    eagerPoster: Boolean(_video),
    loaderProps,
    preload,
    setLoadingState,
    sizes,
    useLoader,
    width,
    mediaWrapperType,
  };

  return (
    <S.Box
      aria-label={getMediaWrapperAriaLabel(
        url,
        image?.title,
        formatMessage,
        languageCode,
      )}
      as={url ? LinkWithLocale : 'div'}
      {...(!!url && { href: url })}
      $MediaWrapperType={mediaWrapperType}
      data-test-id='MediaWrapperBox'
    >
      {_imageDesktop.length > 0 && _imageMobile.length > 0 && (
        <S.ImageWrapper $mediaWrapperType={mediaWrapperType}>
          <MobileImage
            {...imageProps}
            _focalPoint={imageMobile ? mobileImageFocalPoint : focalPoint}
            _image={_imageMobile}
            aspectRatioData={getAspectRatioData('mobile')}
          />
          <DesktopImage
            {...imageProps}
            _focalPoint={focalPoint}
            _image={_imageDesktop}
            aspectRatioData={getAspectRatioData('desktop')}
          />
        </S.ImageWrapper>
      )}
      {_video && (
        <S.VideoWrapper
          $videoPlayOption={videoPlayOption as string}
          data-testid='MediaWrapperVideoWrapper'
        >
          <Video
            controls={false}
            data-test-id='MediaWrapperVideo'
            desktopAspectRatio={getAspectRatioData('desktop')}
            isCountDownType={isCountDownType}
            lazyLoad={lazyLoadVideo}
            mediaUrl={videoUrl}
            mobileAspectRatio={getAspectRatioData('mobile')}
            poster={videoPlayOption === 'AutoplayLoop' ? undefined : poster}
            setLoadingState={setLoadingState}
            title={_video.title}
            videoPlayOption={videoPlayOption}
          />
        </S.VideoWrapper>
      )}
    </S.Box>
  );
};

MediaWrapper.displayName = 'MediaWrapper';
